// Pure pivot for the cost chart: flat (bucket, group) rows → one aligned series per
// group. Kept out of the component so the grouping rules are unit-testable.
//
// Color on this page means PROVIDER, everywhere. So a series' color comes from the
// provider it belongs to, never from a palette of its own:
// - provider:            one line per provider, its fixed color.
// - family (feature, model — each belongs to one provider): lines take their
//   provider's color; siblings of one provider are told apart by MARKER SHAPE
//   (circle, square, triangle, diamond, down-triangle, biggest first), the rest
//   folded into one dotted, marker-less "Other <Provider>" line. Shades were
//   measured and ruled out: the six provider hues already fill the space, so a
//   second shade either turns muddy (outside the validator's lightness band) or
//   collides with another provider (Wiza's best sibling sat 7.5 ΔE from Tavily;
//   15 is the floor). A provider collapsed in the Cost Explorer draws as ONE
//   line — its whole family summed, styled like a provider-mode line.
// - neutral (team, user, surface — each spans every provider): no provider color
//   can mean them, so they're gray lines identified by direct labels, highlighted
//   on hover (Datawrapper: use color only for what you want readers to see).

import type { GroupBy } from '~/components/aiUsageGroupBy';
import { compareProviders, providerMeta } from '~/components/aiUsageFormat';

export type LineStyle = 'solid' | 'dotted';
/** Point marker; 'none' = no markers (the folded "Other" lines), a dot only at the hovered day. */
export type MarkerShape = 'circle' | 'square' | 'triangle' | 'diamond' | 'triangleDown' | 'none';
export type ChartMode = 'provider' | 'family' | 'neutral';
/** What the cost chart plots per bucket: the running total ('additive') or that bucket's own spend. */
export const CHART_VALUE_MODES = ['additive', 'perUnit'] as const;
export type ChartValueMode = (typeof CHART_VALUE_MODES)[number];

export type ChartSeries = {
  key: string;
  /**
   * The series ids (`seriesId`) this line draws: its own, or — for a folded "Other"
   * line — every group folded into it. Lets a hovered table row find its line.
   */
  members: string[];
  label: string;
  color: string;
  lineStyle: LineStyle;
  marker: MarkerShape;
  total: number;
  values: number[];
};

/**
 * One timeseries point as the route returns it: key = the grouped value (null =
 * Unattributed); provider = its provider when the query split by provider, else null.
 */
export type ChartPoint = { bucket: Date; key: string | null; provider: string | null; label: string; costUsd: number };

// Synthetic series keys — can't collide with a real value (provider, feature_key, id…).
export const UNATTRIBUTED_KEY = '__unattributed__';
export const OTHER_KEY = '__other__';
/** Prefix of a collapsed provider's single line: `${PROVIDER_TOTAL_KEY}:${provider}`. */
export const PROVIDER_TOTAL_KEY = '__provider__';

/**
 * The identity of a chart line AND its Cost Explorer row: the grouped value, per
 * provider when the query split by provider (a feature paying two providers is two
 * lines / rows). The separator is a control character no key can contain.
 */
export function seriesId(key: string | null, provider: string | null): string {
  const k = key ?? UNATTRIBUTED_KEY;
  return provider == null ? k : `${provider}\u001f${k}`;
}

/** Neutral-mode lines: the top groups, then the folded rest (lighter, dotted). */
export const MAX_NEUTRAL_SERIES = 6;
export const NEUTRAL_SERIES_COLOR = '#94a3b8';
export const NEUTRAL_OTHER_COLOR = '#cbd5e1';

/** Family mode: sibling marker shapes within one provider's color, biggest line first. */
export const FAMILY_MARKERS: MarkerShape[] = ['circle', 'square', 'triangle', 'diamond', 'triangleDown'];

/** Lines per provider in family mode (one per marker shape) before the rest fold into "Other <Provider>". */
export const LINES_PER_PROVIDER = FAMILY_MARKERS.length;

export function chartModeFor(groupBy: GroupBy): ChartMode {
  if (groupBy === 'provider') return 'provider';
  return groupBy === 'feature' || groupBy === 'model' ? 'family' : 'neutral';
}

/**
 * Whether the chart and the Cost Explorer ask for one line / row per (value,
 * provider): in family mode, so each provider's group adds up exactly (a feature
 * that pays two providers shows under both). Both panels pass it, so their explorer
 * queries stay identical (one request).
 */
export function splitsByProvider(groupBy: GroupBy): boolean {
  return chartModeFor(groupBy) === 'family';
}

type Group = { key: string; provider: string | null; label: string; total: number; byTime: Map<number, number> };
/** How a line looks and is named — everything but the data it draws. */
type LineLook = Pick<ChartSeries, 'key' | 'label' | 'color' | 'lineStyle' | 'marker'>;

/** A feature/model's place in its provider's family: which provider, and its spend rank within it (0 = biggest). */
export type FamilyPlanEntry = { provider: string; rank: number };

/**
 * Rank every group within its provider by spend — the ONE ranking both the chart
 * (which line wears which marker) and the Cost Explorer table (which swatch a row
 * shows) read, so a row and its line always match. Ties break by key, so the order
 * is deterministic. `provider` '' = unknown.
 */
export function planFamilies(
  groups: { key: string; provider: string; costUsd: number }[],
): Map<string, FamilyPlanEntry> {
  const plan = new Map<string, FamilyPlanEntry>();
  const byProvider = new Map<string, { key: string; costUsd: number }[]>();
  for (const g of groups) byProvider.set(g.provider, [...(byProvider.get(g.provider) ?? []), g]);
  for (const [provider, members] of byProvider) {
    members
      .sort((a, b) => b.costUsd - a.costUsd || a.key.localeCompare(b.key))
      .forEach((g, rank) => plan.set(g.key, { provider, rank }));
  }
  return plan;
}

/**
 * The family plan from Cost Explorer rows, keyed by `seriesId`. A row's provider is
 * its own when split by provider, else its biggest provider split.
 */
export function planFromExploreRows(
  rows: { key: string | null; provider: string | null; costUsd: number; byProvider: { provider: string }[] }[],
): Map<string, FamilyPlanEntry> {
  return planFamilies(
    rows.flatMap((r) =>
      r.key != null
        ? [
            {
              key: seriesId(r.key, r.provider),
              provider: r.provider ?? r.byProvider[0]?.provider ?? '',
              costUsd: r.costUsd,
            },
          ]
        : [],
    ),
  );
}

/** A family's color: its provider's, or neutral gray for an unknown provider (''). */
function familyColor(provider: string): string {
  return provider === '' ? NEUTRAL_SERIES_COLOR : providerMeta(provider).color;
}

/** How a family member draws: its provider's color, and its rank's marker — or folded into "Other" (dotted). */
export function familyStyle(entry: FamilyPlanEntry | undefined): {
  color: string;
  lineStyle: LineStyle;
  marker: MarkerShape;
  folded: boolean;
} {
  const color = familyColor(entry?.provider ?? '');
  const rank = entry?.rank ?? Number.POSITIVE_INFINITY;
  const marker = FAMILY_MARKERS[rank];
  return marker != null
    ? { color, lineStyle: 'solid', marker, folded: false }
    : { color, lineStyle: 'dotted', marker: 'none', folded: true };
}

/**
 * Align every group to one sorted set of bucket times (0-filled where a group had
 * no spend), cumulative or per-bucket, and style it per the page's color rule
 * (see the file header). Series are keyed by `seriesId`. `familyPlan` (from
 * `planFromExploreRows`) gives each family-mode series its provider and rank; ones
 * it lacks are ranked here by the chart's own totals, under the provider their
 * points carry (or a neutral "Unknown" family when they carry none), AFTER the
 * plan's own members of that provider, so they never reuse a planned marker.
 * `bucketMs` (the bucket width) fills gaps in the time axis: a bucket nobody spent
 * in still gets its x position (at 0), so sparse series keep their real timing.
 * `collapsedProviders` (family mode) draws each of those providers as one summed line.
 */
export function buildChartSeries(
  points: ChartPoint[],
  {
    groupBy,
    cumulative,
    familyPlan = new Map(),
    bucketMs,
    collapsedProviders = new Set(),
  }: {
    groupBy: GroupBy;
    cumulative: boolean;
    familyPlan?: Map<string, FamilyPlanEntry>;
    /** Bucket width in ms (UTC hours/days are exact); omit to plot only buckets with spend. */
    bucketMs?: number;
    /** Providers collapsed in the Cost Explorer ('' = unknown) — one line each instead of one per member. */
    collapsedProviders?: ReadonlySet<string>;
  },
): { times: number[]; series: ChartSeries[]; mode: ChartMode } {
  const mode = chartModeFor(groupBy);
  const seen = [...new Set(points.map((p) => p.bucket.getTime()))].sort((a, b) => a - b);
  const first = seen[0];
  const last = seen[seen.length - 1];
  const times =
    bucketMs != null && bucketMs > 0 && first != null && last != null
      ? Array.from({ length: Math.round((last - first) / bucketMs) + 1 }, (_, i) => first + i * bucketMs)
      : seen;

  const groups = new Map<string, Group>();
  for (const p of points) {
    const key = seriesId(p.key, p.provider);
    const g = groups.get(key) ?? {
      key,
      provider: p.provider,
      label: p.label,
      total: 0,
      byTime: new Map<number, number>(),
    };
    g.total += p.costUsd;
    g.byTime.set(p.bucket.getTime(), (g.byTime.get(p.bucket.getTime()) ?? 0) + p.costUsd);
    groups.set(key, g);
  }

  const toValues = (byTime: Map<number, number>): number[] => {
    let cum = 0;
    return times.map((t) => {
      const v = byTime.get(t) ?? 0;
      cum += v;
      return cumulative ? cum : v;
    });
  };
  // One line drawing several groups summed: a collapsed provider, or a folded "Other".
  const summedLine = (look: LineLook, members: Group[]): ChartSeries => {
    const byTime = new Map<number, number>();
    for (const g of members) for (const [t, v] of g.byTime) byTime.set(t, (byTime.get(t) ?? 0) + v);
    return {
      ...look,
      members: members.map((g) => g.key),
      total: members.reduce((acc, g) => acc + g.total, 0),
      values: toValues(byTime),
    };
  };
  const byTotal = (a: Group, b: Group) => b.total - a.total;

  if (mode === 'provider') {
    const series = [...groups.values()]
      .sort((a, b) => compareProviders(a.key, b.key))
      .map((g) => {
        const meta = providerMeta(g.key);
        return {
          key: g.key,
          members: [g.key],
          label: meta.label,
          color: meta.color,
          lineStyle: 'solid' as const,
          marker: 'circle' as const,
          total: g.total,
          values: toValues(g.byTime),
        };
      });
    return { times, series, mode };
  }

  if (mode === 'family') {
    // Series the plan lacks (e.g. past the explorer's row limit, or new since its
    // last fetch) rank by our own totals — after the plan's members of their
    // provider, so a planned marker is never reused (past the markers they fold).
    const plannedCount = new Map<string, number>();
    for (const e of familyPlan.values()) plannedCount.set(e.provider, (plannedCount.get(e.provider) ?? 0) + 1);
    const unplanned = new Map(
      [
        ...planFamilies(
          [...groups.values()]
            .filter((g) => !familyPlan.has(g.key))
            .map((g) => ({ key: g.key, provider: g.provider ?? '', costUsd: g.total })),
        ),
      ].map(([key, e]) => [key, { ...e, rank: e.rank + (plannedCount.get(e.provider) ?? 0) }]),
    );
    const planOf = (key: string): FamilyPlanEntry =>
      familyPlan.get(key) ?? unplanned.get(key) ?? { provider: '', rank: 0 };

    const byProvider = new Map<string, Group[]>();
    for (const g of groups.values()) {
      const { provider } = planOf(g.key);
      byProvider.set(provider, [...(byProvider.get(provider) ?? []), g]);
    }
    const series: ChartSeries[] = [];
    for (const provider of [...byProvider.keys()].sort(compareProviders)) {
      const members = (byProvider.get(provider) ?? []).sort((a, b) => planOf(a.key).rank - planOf(b.key).rank);
      const providerLabel = provider === '' ? 'Unknown' : providerMeta(provider).label;
      // Collapsed in the Cost Explorer: the whole family is one line, like its
      // header row there — members keep every id, so hovering that row still lights it.
      if (collapsedProviders.has(provider)) {
        const look: LineLook = {
          key: `${PROVIDER_TOTAL_KEY}:${provider}`,
          label: providerLabel,
          color: familyColor(provider),
          lineStyle: 'solid',
          marker: 'circle',
        };
        series.push(summedLine(look, members));
        continue;
      }
      const named = members.filter((g) => !familyStyle(planOf(g.key)).folded);
      const rest = members.filter((g) => familyStyle(planOf(g.key)).folded);
      for (const g of named) {
        const style = familyStyle(planOf(g.key));
        series.push({
          key: g.key,
          members: [g.key],
          label: g.label,
          color: style.color,
          lineStyle: style.lineStyle,
          marker: style.marker,
          total: g.total,
          values: toValues(g.byTime),
        });
      }
      if (rest.length > 0) {
        const { color, lineStyle, marker } = familyStyle(planOf(rest[0]!.key));
        const label = `Other ${providerLabel} (${rest.length})`;
        series.push(summedLine({ key: `${OTHER_KEY}:${provider}`, label, color, lineStyle, marker }, rest));
      }
    }
    return { times, series, mode };
  }

  const ranked = [...groups.values()].sort(byTotal);
  const top = ranked.slice(0, MAX_NEUTRAL_SERIES);
  const rest = ranked.slice(MAX_NEUTRAL_SERIES);
  const series: ChartSeries[] = top.map((g) => ({
    key: g.key,
    members: [g.key],
    label: g.label,
    color: NEUTRAL_SERIES_COLOR,
    lineStyle: 'solid',
    marker: 'circle',
    total: g.total,
    values: toValues(g.byTime),
  }));
  if (rest.length > 0) {
    const look: LineLook = {
      key: OTHER_KEY,
      label: `Other (${rest.length})`,
      color: NEUTRAL_OTHER_COLOR,
      lineStyle: 'dotted',
      marker: 'none',
    };
    series.push(summedLine(look, rest));
  }
  return { times, series, mode };
}

/**
 * The chart lines drawing any of the focused ids (`seriesId`s, from a hovered Cost
 * Explorer row or group) — a row folded into an "Other" line lights that line. Ids
 * no line draws (e.g. left over from a previous grouping) match nothing.
 */
export function focusedSeriesKeys(series: ChartSeries[], focus: readonly string[]): ReadonlySet<string> {
  return new Set(series.filter((s) => s.members.some((m) => focus.includes(m))).map((s) => s.key));
}

/** Gridline intervals the y axis may use: fewer reads empty, more crowds the labels. */
const MIN_Y_INTERVALS = 3;
const MAX_Y_INTERVALS = 6;

/**
 * The y axis for a data max: the LOWEST ceiling reachable in 3–6 equal steps of a
 * clean size (1, 2 or 5 × 10^n), so the top gridline hugs the highest point. A bare
 * 1/2/5 ceiling left up to 60% of the plot empty ($510 → $1K); this caps it near
 * 25% (measured over $1–$100K), about 11% on average. Ties take the bigger step
 * (fewer gridlines). Nothing to plot → $0–$1.
 */
export function niceAxis(dataMax: number): { max: number; step: number } {
  if (!(dataMax > 0)) return { max: 1, step: 0.2 };
  const pow = Math.pow(10, Math.floor(Math.log10(dataMax)));
  let best: { max: number; step: number } | null = null;
  for (const p of [pow / 10, pow]) {
    for (const m of [1, 2, 5]) {
      const step = m * p;
      // The epsilon keeps float noise (0.3 / 0.1 = 2.9999…) from costing a step.
      const intervals = Math.ceil(dataMax / step - 1e-9);
      if (intervals < MIN_Y_INTERVALS || intervals > MAX_Y_INTERVALS) continue;
      const max = intervals * step;
      if (best == null || max < best.max || (max === best.max && step > best.step)) best = { max, step };
    }
  }
  // Unreachable — every max has a 3–6 step fit among these — but never draw a
  // zero-height axis if that ever changes.
  return best ?? { max: dataMax, step: dataMax / MIN_Y_INTERVALS };
}

/**
 * Y labels for an axis stepping by `step`: compact ($1.2K), with as many decimals
 * as a sub-dollar step needs so no two gridlines read the same ($0.05, $0.1, $0.15
 * — at a fixed 1 decimal a $0.05 step read "$0.1, $0.1, $0.2, $0.2").
 */
export function yAxisLabeler(step: number): (value: number) => string {
  const decimals = Math.max(1, Math.ceil(-Math.log10(step) - 1e-9));
  const format = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: decimals });
  return (value) => `$${format.format(value)}`;
}

/**
 * Spread end-of-line label positions apart so none overlap: keep each as close to
 * its line's end as possible, at least `gap` px from its neighbours, inside
 * [top, bottom]. Returns positions in the input order.
 */
export function spreadLabels(
  ys: number[],
  { gap, top, bottom }: { gap: number; top: number; bottom: number },
): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  // Downward pass: push each label below its predecessor.
  for (let k = 0; k < order.length; k++) {
    const prev = order[k - 1];
    const cur = order[k];
    if (cur == null) continue;
    cur.y = Math.max(cur.y, top, prev != null ? prev.y + gap : top);
  }
  // Upward pass: pull back inside the bottom edge, keeping the gaps.
  for (let k = order.length - 1; k >= 0; k--) {
    const next = order[k + 1];
    const cur = order[k];
    if (cur == null) continue;
    cur.y = Math.min(cur.y, bottom, next != null ? next.y - gap : bottom);
  }
  const out = new Array<number>(ys.length);
  for (const o of order) out[o.i] = o.y;
  return out;
}
